import Site from 'es/app/business-logic/domain-models/Site';
import { IUniversalSearchCriteria } from 'es/app/business-logic/services/universal-search/UniversalSearch';
import { createSiteRouteMap } from 'es/app/views/site/RouteMap';
import ApolloProvider from 'es/core/net/ApolloProvider';
import Guid from 'es/core/types/Guid';
import { createl10n } from 'es/i18n';
import { SITE_ENTITY_SEARCH_BY_ID } from './graphql/facility.siteEntitySearchById';
import { SITE_ENTITY_SEARCH_BY_TEXT } from './graphql/facility.siteEntitySearchByText';
import {
    SiteEntitySearchByIdQuery,
    SiteEntitySearchByIdQueryVariables,
    SiteEntitySearchByTextQuery,
    SiteEntitySearchByTextQueryVariables,
} from 'es/app/__generated__/global';

export interface ISiteEntitySearchResults {
    processes?: ISiteEntitySearchResult[];
    processVariables?: ISiteEntitySearchResult[];
    processSchemas?: ISiteEntitySearchResult[];
}

export interface ISiteEntitySearchResult {
    id: Guid;
    label: string;
    description: string;
    route: string;
}

interface IProcessSearchResult {
    id: Guid;
    name: string;
    description?: string | undefined;
}

interface IProcessVariableSearchResult {
    id: Guid;
    name: string;
    description?: string | undefined;
    process: {
        id: Guid;
    };
}

interface IProcessSchemaSearchResult {
    id: Guid;
    name: string;
    description?: string | undefined;
    process: {
        id: Guid;
    };
}

const l10n = createl10n('app/business-logic/services', 'universalSearch');

export async function universalSiteEntitySearch(
    criteria: IUniversalSearchCriteria,
    site: Site
): Promise<ISiteEntitySearchResults> {
    if (criteria.id) {
        return await getSiteEntitySearchById(criteria.id, site);
    }
    return await getSiteEntitySearchByText(criteria.text ?? '', site);
}

async function getSiteEntitySearchById(id: string, site: Site): Promise<ISiteEntitySearchResults> {
    /**
     * We ignore errors from this in our @see ApolloProvider.ts
     */
    const queryText = SITE_ENTITY_SEARCH_BY_ID;

    const { data } = await ApolloProvider.facility(site.companyAlias, site.facilityAlias).query<
        SiteEntitySearchByIdQuery,
        SiteEntitySearchByIdQueryVariables
    >({
        query: queryText,
        variables: {
            processFilter: { id_in: [id], include_hidden: true },
            processVariableFilter: { id_in: [id] },
            processSchemaFilter: { id_in: [id] },
        },
    });

    return {
        processes: mapProcesses(site, data?.facility?.processes as IProcessSearchResult[] | undefined),
        processVariables: mapVariables(
            site,
            data?.facility?.processVariables as IProcessVariableSearchResult[] | undefined
        ),
        processSchemas: mapSchemas(site, data?.facility?.processSchemas),
    };
}

async function getSiteEntitySearchByText(any_contains: string, site: Site): Promise<ISiteEntitySearchResults> {
    const queryText = SITE_ENTITY_SEARCH_BY_TEXT;

    const { data } = await ApolloProvider.facility(site.companyAlias, site.facilityAlias).query<
        SiteEntitySearchByTextQuery,
        SiteEntitySearchByTextQueryVariables
    >({
        query: queryText,
        variables: {
            processFilter: { any_contains },
        },
        // We have to ignore errors since we don't really know what we're searching for... and if we're searching with
        // an ID, we effectively say to the server (e.g.) "Give me the monitor with the id xxxx-yyyy" - but if that
        // ID belongs to something that's NOT a monitor, the result will be a 404 (which the Graph interprets as an
        // error).
        errorPolicy: 'ignore',
    });

    return {
        processes: mapProcesses(site, data?.facility?.processes as IProcessSearchResult[] | undefined),
    };
}

function mapProcesses(site: Site, processes: IProcessSearchResult[] | undefined) {
    if (!processes?.length) return [];

    const results: ISiteEntitySearchResult[] = [];
    const routeMap = createSiteRouteMap(site.companyAlias, site.facilityAlias);

    processes?.forEach(process => {
        const { id, name } = process;

        results.push({
            id: process.id,
            label: name,
            description: l10n('site.description', { site: site.toString() }),
            route: routeMap.processes.edit(id),
        });
    });

    return results;
}

function mapVariables(
    site: Site,
    processVariables: IProcessVariableSearchResult[] | undefined
): ISiteEntitySearchResult[] {
    if (!processVariables?.length) return [];

    const routeMap = createSiteRouteMap(site.companyAlias, site.facilityAlias);

    return processVariables.map(processVariable => {
        const { name, process } = processVariable;
        const processId = process?.id;

        return {
            id: processVariable.id,
            label: name,
            description: l10n('site.description', { site: site.toString() }),
            route: routeMap.processes.variables(processId).edit(processVariable.id).root,
        };
    });
}

function mapSchemas(site: Site, processSchemas: IProcessSchemaSearchResult[] | undefined): ISiteEntitySearchResult[] {
    if (!processSchemas?.length) return [];

    const routeMap = createSiteRouteMap(site.companyAlias, site.facilityAlias);

    return processSchemas.map(processSchema => {
        const { name, process } = processSchema;
        const processId = process?.id;

        return {
            id: processSchema.id,
            label: name,
            description: l10n('site.description', { site: site.toString() }),
            route: routeMap.processes.processSchemas(processId).edit(processSchema.id).root,
        };
    });
}
